/**
 * ``shortId`` — a compact, human-readable handle for a long opaque entity id
 * (a ULID / UUID) used as a *display* affordance on Detections + Alerts
 * surfaces (REQ-UI-061 / REQ-UI-062 / REQ-UI-066 / REQ-UI-067).
 *
 * Detection report-ids and alert-ids are 26-char Crockford ULIDs (or UUIDs);
 * shown in full they dominate a dense operator row and a tidy detail panel and
 * are near-impossible to eyeball-match. ``shortId`` derives a short, prefixed
 * tail — e.g. ``DET-7Q5FAV`` for a detection, ``ALT-9G2X1B`` for an alert — that
 * is glanceable, distinguishes the two entity kinds at a glance (the prefix),
 * and is stable for a given id. ULIDs sort lexicographically by creation time,
 * so the trailing characters carry the most entropy — taking the **last** 6
 * gives a handle that differs reliably between two ids created close together.
 *
 * The short id is a *display convenience*, never an identifier: callers keep the
 * **full** id available (it is the selection-bus key + the value shown on hover
 * / ``title`` and copied) so an operator can still cross-reference logs by the
 * exact id (CLAUDE.md §8.1 — never hide what the system is keyed on). It is not
 * guaranteed collision-free across the whole id space — it exists to be read,
 * not to look up.
 *
 * Cross-references:
 *   - REQ-UI-061 / REQ-UI-062 (Detections / Alerts list rows).
 *   - REQ-UI-066 / REQ-UI-067 (Detection / Alert Entity Details).
 *   - CLAUDE.md §8.1 (operator-visibility — the full id stays reachable).
 */

/**
 * Number of trailing characters of the id taken for the short handle. Six is
 * enough to read + reliably distinguish ULIDs created close together (the
 * trailing chars carry the most entropy) without bloating a dense row.
 */
const SHORT_ID_TAIL_LENGTH = 6;

/**
 * Build a short, prefixed display handle from an entity id.
 *
 * @param ulid - The full entity id (a ULID / UUID). When empty/whitespace-only
 *   it has no meaningful tail to show, so the raw value is returned verbatim
 *   (no prefix) — a graceful fallback rather than a misleading ``DET-`` stub.
 * @param prefix - The entity-kind tag prepended to the tail (e.g. ``"DET"`` /
 *   ``"ALT"``). Joined with a hyphen.
 * @returns ``${prefix}-${last6Uppercased}`` for a non-empty id; the raw id
 *   itself when it is empty/blank. An id **shorter** than
 *   {@link SHORT_ID_TAIL_LENGTH} keeps all its characters (``slice`` clamps),
 *   still uppercased + prefixed, so a short non-empty id still reads as a handle.
 *
 * @example
 * shortId("01J9Q5FAV7Q5FAV7Q5FAV7Q5FA", "DET"); // "DET-Q5FAFA" → tail, uppercased
 * shortId("", "DET");                            // "" (graceful fallback)
 *
 * Cross-references:
 *   - REQ-UI-061 / REQ-UI-062 / REQ-UI-066 / REQ-UI-067.
 */
export function shortId(ulid: string, prefix: string): string {
  // No meaningful tail to derive — return the raw value rather than a bare,
  // misleading ``${prefix}-`` stub (graceful degradation, CLAUDE.md §8.1).
  if (ulid.trim() === "") {
    return ulid;
  }
  const tail = ulid.slice(-SHORT_ID_TAIL_LENGTH).toUpperCase();
  return `${prefix}-${tail}`;
}
